// import event Bus
import maEventBus from '@/components/eventbus/index.js'

// utils
import debounce from '@/utils/debounce'

// constants
import { MA_WARRANTY_WATER_MOBILIER, VETUSTED } from '@/constants'

// components
import maForm from '@/components/form'
import maEventbus from '@/components/eventbus'
import maModal from '@/components/modal'

// vuex
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'

// types
import {
  WARRANTY_MODULE_NAME,
  WARRANTY_GET_SINISTER_SUMMARY,
  WARRANTY_UPDATE_CREATED,
  WARRANTY_UPDATE_PROPS
} from '@/store/types'

// api
import { apiGetForm } from '@/services/API/menus'
import { apiPostDamage, apiPatchDamage } from '@/services/API/damages'

/**
 * @module
 * @name formGeneral
 */
export default {
  name: 'formGeneral',
  props: {
    formData: Object,
    keysFormCateories: Array,
    selectedCategoriesForm: Object
  },
  /**
   * @property {Object} form Object retrieved from backend which contains
   *                         dynamic form of the current view and his inputs.
   * @property {Object} damage Current damage. It's saved in component local
   *                           state
   * @property {Object} damage.rep_type_code Kwnon field which has to be included
   *                                         manually. It's form.rep_type_code,
   *                                         that changes every time the form
   *                                         has a different combination.
   * @property {Object} activeInput DOM HTML Object of focused input.
   * @property {String} activeInputValue Value of focused input.
   */
  data: function() {
    return {
      form: {},
      damage: {
        asset_damages: 0
      },
      activeInput: '',
      activeInputValue: '',
      addDamageBtnIsHidden: false,
      // Vétusté forzada manualmente por el usuario. Se mantiene aparte de damage
      // porque updateForm() lo recalcula en el back y pisaría el valor introducido.
      manualVetuste: null
    }
  },

  components: {
    maForm,
    maEventbus,
    maModal
  },

  /**
   * @property {Boolean} comWarrantyCreated Computed of warranty/created (get/set).
   * @property {String} warrantyId Route id
   * @property {Boolean} disableAddDamageButton Returns true if sinisterId,
   *                                            coordinates or zipCode don't exist.
   * @property {String} warrantyid Route warranty group id.
   * @property {String} warrantyCategory Route warranty subgroup id.
   * @property {Boolean} isMobilierRoute Checks if the route name is mobilier
   */
  computed: {
    ...mapState({
      agentView: state => state.agentView
    }),
    ...mapState(WARRANTY_MODULE_NAME, {
      stateWarrantyCreated: 'created',
      stateWarrantySinisterId: 'sinisterId',
      stateWarrantySecondaryId: 'secondaryId',
      stateWarrantyAddress: 'address',
      stateWarrantyZipCode: 'zipCode',
      stateWarrantyCoordinates: 'coordinates',
      stateWarrantyMoreThanTwoYears: 'moreThanTwoYears',
      stateWarrantyPropertyType: 'propertyType',
      stateWarrantyResidentType: 'residentType',
      stateWarrantyaddress: 'address'
    }),
    ...mapGetters(WARRANTY_MODULE_NAME, {
      getWarrantySinisterHasAddress: 'sinisterHasAddress'
    }),

    comWarrantyCreated: {
      get() {
        return this.stateWarrantyCreated
      },
      set(v) {
        this.mutWarrantyUpdateCreated(v)
      }
    },

    warrantyId: function() {
      return this.$route.meta.id
    },

    disableAddDamageButton: function() {
      return (
        !this.stateWarrantySinisterId ||
        this.stateWarrantySinisterId.lenght <= 0
      )
    },

    warrantyid: function() {
      return this.$route.meta.warrantyid || ''
    },

    warrantyCategory: function() {
      return this.$route.meta.warranty_subtype_id
    },

    isMobilierRoute() {
      return this.$route.name === MA_WARRANTY_WATER_MOBILIER
    },

    externalDamageId: function() {
      return this.FNOLFlow.params.externalDamageId
        ? this.FNOLFlow.params.externalDamageId
        : null
    }
  },
  methods: {
    ...mapActions(WARRANTY_MODULE_NAME, {
      actWarrantyGetSinisterSummary: WARRANTY_GET_SINISTER_SUMMARY,
      actWarrantyUpdateProps: WARRANTY_UPDATE_PROPS
    }),
    ...mapMutations(WARRANTY_MODULE_NAME, {
      mutWarrantyUpdateCreated: WARRANTY_UPDATE_CREATED
    }),
    openModal(ref) {
      this.$refs[ref].$data.showModal = true
    },

    /**
     * @function
     * @name handleFormChange
     * @param {Object} event.inputId Input id, matches with this.damage's
     *                               properties. They've the same name as the
     *                               ones sent to backend.
     * @param {Object} event.inputValue Input value that fired the event.
     * @description Damage is updated locally on input change.
     */
    handleFormChange: function(event) {
      if (event.inpTextNumbers) {
        const inputValParsed = event.inputVal.replace(',', '.')
        this.damage[event.inputId] = inputValParsed
        if (event.inputId === VETUSTED) {
          this.manualVetuste = inputValParsed
        }
      } else {
        this.damage[event.inputId] = event.inputVal
      }
    },

    /**
     * @function
     * @name handleNestedComponents
     * @description Debounced function (Important. Just first input event is catched).
     *              Form has to be updated always because rep_type_code changes
     *              depending on input value combination.
     */
    handleNestedComponents: debounce(function(event) {
      // Al editar la vétusté no recalculamos en el back: pisaría el valor manual
      // (p. ej. clientes RAN o deducción del 25% recuperable en VAN).
      if (event && event.inputId === VETUSTED) {
        return
      }
      // Cualquier otro campo que recalcula (ej. Age du bien) descarta el valor
      // manual para que prevalezca la vétusté calculada por el back.
      this.manualVetuste = null
      this.updateForm()
    }, 250),

    /**
     * * Disable dynamic form inputs.
     * * Update form inputs from backend.
     * * Wait until next tick and then set default values, updating local state.
     * @function
     * @name updateForm
     */
    updateForm: function() {
      const sinisterInfo = {
        sinisterId: this.stateWarrantySinisterId,
        zipCode: this.stateWarrantyZipCode
      }
      this.damage = { ...this.damage, ...this.selectedCategoriesForm }
      this.damage['SINISTER_INFO'] = sinisterInfo
      const body = { ...this.damage }
      const formInputs = document.querySelectorAll('.warranty__content input')

      delete body['asset_damages']

      formInputs.forEach(function(el) {
        el.disabled = true
      })
      return apiGetForm(this.warrantyId, body)
        .then(data => {
          this.form = null
          this.$nextTick(() => {
            this.form = data.form
            this.filterCategories()
            this.setDefaultValues()
            this.$nextTick(() => {
              this.handleUpdateForm()
            })
          })
        })
        .finally(() => {
          formInputs.forEach(function(el) {
            el.disabled = false
          })
        })
    },
    filterCategories() {
      this.keysFormCateories.forEach(cat => {
        this.form.regions = this.form.regions.map(region => {
          if (region.name === 'WATER_MOBILIER_DAMAGES') {
            region.inputs = region.inputs.filter(input => input.name !== cat)
          }
          return region
        })
      })
    },
    updateFormStart: function() {
      this.form = this.formData
      this.setDefaultValues()
      this.$nextTick(() => {
        this.handleUpdateForm()
      })
    },

    /**
     * @function
     * @name clearForm
     * @description Set form and damage to initial values, empty objects. Then,
     *              it updates form.
     */
    clearForm: function() {
      this.damage = {}
      this.form = {}
      this.manualVetuste = null
      this.updateForm()
    },

    /**
     * @function
     * @name setWatchers
     * @description updateForm and clearForm when warrantyId changes. This means,
     *              when a new sinister is created.
     */
    setWatchers: function() {
      let vm = this
      vm.$watch('warrantyId', function() {
        this.updateForm()
        this.clearForm()
      })
    },

    /**
     * @function
     * @name setDefaultValues
     * @description Set rep_type_code, and loop on received dynamic form input
     *              looking for selectedValue field. If exists, corresponding
     *              property within damage object is filled in with it.
     */
    setDefaultValues: function() {
      this.damage = {}
      this.damage.asset_damages = this.form.asset_damages || ''

      this.form.regions.forEach(region => {
        region.inputs.forEach(input => {
          if (input.selectedValue) {
            this.damage[input.name] = input.selectedValue
          }
        })
      })
    },

    /**
     * @function
     * @name handleAddDamage
     * @description Manage adding damage. If sinister is not created, the request
     *              uses POST method sending additional info necessary for the
     *              sinister register. Else uses PATCH.
     */
    handleAddDamage: function() {
      this.updateForm().then(() => {
        // updateForm() recalcula la vétusté en el back; si el usuario la forzó
        // manualmente, reponemos su valor antes de enviar el daño.
        if (this.manualVetuste !== null) {
          this.damage[VETUSTED] = this.manualVetuste
        }
        let damageBody = {
          sinister_id: this.stateWarrantySinisterId,
          event_id: this.stateWarrantySecondaryId,
          category: this.warrantyid,
          subcategory: this.warrantyCategory,
          form_info: { ...this.damage, ...this.selectedCategoriesForm },
          external_damage_id: this.externalDamageId
        }

        if (!this.comWarrantyCreated) {
          damageBody = {
            ...damageBody
          }

          // 400 campos faltan, 500 generico
          apiPostDamage(damageBody)
            .then(() => {
              this.throwNotification(
                this.$t('notifications.addDamage.title'),
                this.$t('notifications.addDamage.body'),
                'OK'
              )
              maEventbus.$emit('damage:closeAccordion')
              this.comWarrantyCreated = true
              this.getWarrantySinisterHasAddress &&
                this.actWarrantyUpdateProps()
              this.actWarrantyGetSinisterSummary()
            })
            .catch(err => {
              const errs = [400, 500, 409]
              const errCode =
                errs.find(errCode => err.response.status === errCode) || 0
              this.throwNotification(
                this.$t(`notifications.error.${errCode}.title`),
                err.response.data.detail,
                'KO'
              )
            })
        } else {
          apiPatchDamage(damageBody)
            .then(() => {
              maEventbus.$emit('damage:closeAccordion')
              maEventBus.$emit('summary:collapse')
              this.throwNotification(
                this.$t('notifications.addDamage.title'),
                this.$t('notifications.addDamage.body'),
                'OK'
              )
              this.actWarrantyGetSinisterSummary()
            })
            .catch(err => {
              const errs = [400, 500, 409]
              const errCode =
                errs.find(errCode => err.response.status === errCode) || 0
              this.throwNotification(
                this.$t(`notifications.error.${errCode}.title`),
                err.response.data.detail,
                'KO'
              )
            })
        }
      })
    },

    handleInputFocus: function(value) {
      const focusedInput = document.querySelector('input:focus')
      if (document.querySelector('input:focus') !== null) {
        this.activeInput = focusedInput
      }
    },

    handleUpdateForm: function() {
      if (this.activeInput !== '') {
        const activeInputType = this.activeInput.getAttribute('type')
        const activeInputName = this.activeInput.getAttribute('name')
        const activeInputValue = this.damage[activeInputName]
        // REFACTOR MOVER A FORM
        if (
          activeInputType === 'checkbox' &&
          this.activeInput.hasAttribute('isSelect')
        ) {
          document.querySelector('input[name=' + activeInputName + ']').focus()
          document.querySelector('input[name=' + activeInputName + ']').blur()
        } else if (activeInputType === 'text') {
          document.querySelector('input[name=' + activeInputName + ']').focus()
        } else {
          const el = document.querySelector(
            'input[value="' + activeInputValue + '"]'
          )
          if (el) el.focus()
        }
      }
    }
  },

  /**
   * * Initial update form.
   * * Set watchers.
   * * Event handlers.
   * @function
   * @name mounted
   */
  mounted: function() {
    this.updateFormStart()
    this.setWatchers()
    /**
     * @event
     * @name iframeUptec:visible
     * @description When the iframe is visible, hide add damage button
     */
    maEventBus.$on('iframeUptec:visible', () => {
      this.addDamageBtnIsHidden = true
    })

    /**
     * @event
     * @name iframeUptec:hidden
     * @description When the iframe is hidden, display the add damage button
     */
    maEventBus.$on('iframeUptec:hidden', () => {
      this.addDamageBtnIsHidden = false
    })

    maEventbus.$on(
      'new:sinister',
      /**
       * new sinister event
       *
       * @event
       * @name new:sinister
       * @description Clear form
       */
      () => {
        this.clearForm()
      }
    )

    maEventBus.$on(
      'openSurfaceModal',
      /**
       * inputWarranty focus event
       *
       * @event
       * @name inputWarranty:focus
       * @description On input focus, the value is emitted.
       */
      () => {
        this.$refs.surfaceModal.$data.showModal = true
      }
    )

    maEventbus.$on(
      'inputWarranty:focus',
      /**
       * inputWarranty focus event
       *
       * @event
       * @name inputWarranty:focus
       * @description On input focus, the value is emitted.
       */
      $event => {
        this.handleInputFocus($event)
      }
    )
  }
}
